import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  EMPTY_ENTITY_ORG_CONFIG,
  EntityOrgConfig,
  OrgConfig,
  fetchOrgConfig,
  pickEntityOrgConfig,
} from "../repo/configuration";

interface OrgConfigContextValue {
  config: OrgConfig;
  loaded: boolean;
}

const OrgConfigContext = createContext<OrgConfigContextValue>({
  config: {},
  loaded: false,
});

export const OrgConfigProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [config, setConfig] = useState<OrgConfig>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchOrgConfig()
      .then((data) => {
        if (!cancelled) setConfig(data);
      })
      .catch(() => {
        if (!cancelled) setConfig({});
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => ({ config, loaded }), [config, loaded]);

  return (
    <OrgConfigContext.Provider value={value}>
      {children}
    </OrgConfigContext.Provider>
  );
};

export function useOrgConfig(): OrgConfigContextValue {
  return useContext(OrgConfigContext);
}

export function useEntityOrgConfig(entity: string): EntityOrgConfig {
  const { config } = useOrgConfig();
  return useMemo(() => pickEntityOrgConfig(config, entity), [config, entity]);
}

export { EMPTY_ENTITY_ORG_CONFIG };
